import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import AnimatedLine from '../components/AnimatedLine';
import { ArrowLeft } from 'lucide-react';

const IntelligenceBlogPost = ({ title, category, readTime, publishDate, content, keyTakeaways }) => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      {/* Back Navigation */}
      <section className="pt-32 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/intelligence" 
            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <ArrowLeft size={20} />
            Back to Intelligence
          </Link>
        </div>
      </section>

      {/* Article Header */}
      <section className="px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-sm text-purple-400 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              {category}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {title}
            </h1>
            <div className="flex items-center gap-4 text-white/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              <span>{publishDate}</span>
              <span>•</span>
              <span>{readTime}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatedLine />

      {/* Article Content */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-invert prose-lg max-w-none"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {content.map((section, idx) => (
              <div key={idx} className="mb-12">
                {section.heading && (
                  <h2 className="text-3xl font-bold mb-6 text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {section.heading}
                  </h2>
                )}
                {section.paragraphs.map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-white/70 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <AnimatedLine />

      {/* Key Takeaways */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="border border-white/10 rounded-lg p-8 bg-white/5"
          >
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Key Takeaways
            </h3>
            <ul className="space-y-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              {keyTakeaways.map((takeaway, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="text-purple-400 mt-1">→</span>
                  <span className="text-white/70">{takeaway}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <AnimatedLine />

      {/* Footer CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Ready to Build Your Media Moat?
            </h3>
            <p className="text-white/70 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
              Let's engineer attention for your brand.
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Apply for Sprint
            </Link>
          </motion.div>
        </div>
      </section>

      <AnimatedLine />

      {/* Footer */}
      <footer className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
            © 2024 AttentionLabs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default IntelligenceBlogPost;
